import React, { memo, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

const STATIC_ANIMATION_SLOWDOWN = 1.5;

/**
 * Helper to parse hex values from color code
 * @param {string} hex value (from theme) for color
 * @returns {{r: number, g: number, b: number}|null} gradient color for sphere
 */
const hexToRgb = (hex) => {
  if (!hex || hex.length < 7) {
    console.error('hex color code not provided for gradient');
    return null;
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
};

const LoginWrapperSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg">
    <defs>
      <filter id="goo">
        <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="blur" />
        <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -8" result="goo" />
        <feBlend in="SourceGraphic" in2="goo" />
      </filter>
    </defs>
  </svg>
);

/**
 * @typedef SphereConfig
 * @property {string} id
 * @property {string} color
 * @property {number} opacity=1
 * @property {{
 *  type: 'circle' | 'vertical' | 'horizontal',
 *  duration: number,
 *  reverse?: boolean,
 *  originOffset?: {
 *    xPercent?: number, yPercent?: number, xPercentOffset?: number, yPercentOffset?: number
 *  }
 *  } | {
 *    type: 'follow',
 *    lerpFactor: number
 *  }} animation
 * @property [number] size=1
 * @property {{left?: number, top?: number}} [initialOffsetPercentage]
 */

/**
 * @type SphereConfig[]
 */
const sphereConfigs = [
  // g1: moveVertical
  {
    id: 'g1',
    color: '#fcfaf4',
    initialOffsetPercentage: { top: 10, left: 10 },
    animation: { type: 'vertical', duration: STATIC_ANIMATION_SLOWDOWN * 3 }
  },
  // g2: moveInCircle reverse (origin calc(50% - 400px))
  {
    id: 'g2',
    color: '#1890ff',
    initialOffsetPercentage: { top: 10, left: 10 },
    animation: {
      type: 'circle',
      duration: STATIC_ANIMATION_SLOWDOWN * 2,
      reverse: true,
      originOffset: { xPercent: 0.5, xPercentOffset: -400, yPercent: 0.5 }
    }
  },
  // g3: moveInCircle (origin calc(50% + 400px))
  {
    id: 'g3',
    color: '#00c2cc',
    initialOffsetPercentage: { top: 10, left: 10 },
    animation: {
      type: 'circle',
      duration: STATIC_ANIMATION_SLOWDOWN * 4,
      originOffset: { xPercent: 0.5, xPercentOffset: 400, yPercent: 0.5 }
    }
  },
  // g4: moveHorizontal (origin calc(50% - 200px))
  {
    id: 'g4',
    color: '#fcfaf4',
    opacity: 0.7,
    initialOffsetPercentage: { top: 10, left: 10 },
    animation: {
      type: 'horizontal',
      duration: STATIC_ANIMATION_SLOWDOWN * 4,
      originOffset: { xPercent: 0.5, xPercentOffset: -200, yPercent: 0.5 }
    }
  },
  // g5: moveInCircle (large, different offset/origin)
  {
    id: 'g5',
    color: '#01b4bd',
    size: 2,
    initialOffsetPercentage: { top: -30, left: -30 },
    animation: {
      type: 'circle',
      duration: STATIC_ANIMATION_SLOWDOWN * 2,
      originOffset: { xPercent: 0.5, xPercentOffset: -800, yPercent: 0.5, yPercentOffset: 200 }
    }
  },
  // follower
  {
    id: 'follower',
    color: '#9881f3',
    opacity: 0.7,
    size: 1.25,
    initialOffsetPercentage: { top: -50, left: -50 },
    animation: { type: 'follow', lerpFactor: 0.015 }
  }
];

const LoginWrapperBackgroundContainer = styled.div`
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(to right top, #096dd9, 70%, #00c2cc);
  position: fixed;
  top: 0;
  left: 0;
  isolation: isolate; /* Create new stacking context for filter */
`;

const StyledCanvas = styled.canvas`
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  filter: url(#goo) blur(40px);

  @media (prefers-reduced-motion) {
    display: none;
  }
`;

class AnimationSphere {
  /**
   * @param {SphereConfig} options
   * @param {number} canvasWidth
   * @param {number} canvasHeight
   */
  constructor({ id, color, opacity = 1, animation, size = 1, initialOffsetPercentage }, canvasWidth, canvasHeight) {
    this.id = id;
    this.color = color;
    this.opacity = opacity;
    this.animation = animation;
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.radius = Math.min(canvasWidth, canvasHeight) * size;

    const initialOffsetX = ((initialOffsetPercentage?.left ?? 0) / 100) * canvasWidth;
    const initialOffsetY = ((initialOffsetPercentage?.top ?? 0) / 100) * canvasHeight;
    this.baseX = canvasWidth / 2 + initialOffsetX;
    this.baseY = canvasHeight / 2 + initialOffsetY;

    if (this.animation.type === 'follow') {
      this.targetX = this.baseX;
      this.targetY = this.baseY;
      this.currentX = this.baseX;
      this.currentY = this.baseY;
    } else {
      this.x = this.baseX;
      this.y = this.baseY;
      this.originX = this.baseX;
      this.originY = this.baseY;

      // If origin was % based in original CSS, calculate relative to element size approx
      if (this.animation.originOffset?.xPercent) {
        this.originX = this.canvasWidth * this.animation.originOffset.xPercent;
      }
      if (this.animation.originOffset?.yPercent) {
        this.originY = this.canvasHeight * this.animation.originOffset.yPercent;
      }
      if (this.animation.originOffset?.xPercentOffset) {
        this.originX += this.animation.originOffset.xPercentOffset;
      }
      if (this.animation.originOffset?.yPercentOffset) {
        this.originY += this.animation.originOffset.yPercentOffset;
      }
    }
  }

  /**
   * @param {number} deltaTime
   * @param {number }time
   * @param {x: number, y: number} mousePos
   */
  update(deltaTime, time, mousePos) {
    const speedFactor = 1 / (this.animation.duration * 1000); // Convert duration (s) to frequency
    const angle = time * speedFactor * 2 * Math.PI * (this.animation.reverse ? -1 : 1);

    switch (this.animation.type) {
      case 'vertical': {
        // Approximate moveVertical: translateY(-50%) to translateY(50%)
        const amplitude = this.canvasHeight * 0.2; // Adjust amplitude as needed
        this.x = this.baseX;
        this.y = this.baseY + amplitude * Math.sin(angle);
        break;
      }
      case 'horizontal': {
        // Approximate moveHorizontal: translateX(-50% to 50%), translateY(-10% to 10%)
        const amplitudeX = this.canvasWidth * 0.2; // Adjust amplitude
        const amplitudeY = this.canvasHeight * 0.05; // Adjust amplitude
        this.x = this.baseX + amplitudeX * Math.sin(angle);
        this.y = this.baseY + amplitudeY * Math.sin(angle); // Link Y movement to X for simplicity
        break;
      }
      case 'circle': {
        // Approximate moveInCircle around the calculated origin
        const radiusX = this.baseX - this.originX;
        const radiusY = this.baseY - this.originY;
        // Use radiusX as the primary distance, assuming near-circular path from CSS origin offsets
        const orbitRadius = Math.abs(radiusX || radiusY || this.canvasWidth * 0.2); // Fallback radius
        this.x = this.originX + orbitRadius * Math.cos(angle);
        this.y = this.originY + orbitRadius * Math.sin(angle);
        break;
      }
      case 'follow': {
        // Update target position smoothly if mousePos is available
        if (mousePos) {
          this.targetX = mousePos.x;
          this.targetY = mousePos.y;
        }
        // Lerp current position towards target
        this.currentX += (this.targetX - this.currentX) * this.animation.lerpFactor;
        this.currentY += (this.targetY - this.currentY) * this.animation.lerpFactor;
        this.x = this.currentX;
        this.y = this.currentY;
        break;
      }
      default:
        break;
    }
  }

  /**
   * @param {RenderingContext} ctx
   */
  draw(ctx) {
    const rgb = hexToRgb(this.color);
    if (!rgb) {
      return;
    }

    // Create the radial gradient (matches original CSS gradientColors)
    const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius);
    gradient.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`); // Center color
    gradient.addColorStop(0.5, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`); // Fade out at 50% radius
    gradient.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`); // Fully transparent at edge

    ctx.save(); // Save context state
    ctx.globalAlpha = this.opacity;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore(); // Restore context state (especially globalAlpha)
  }
}

export const LoginBackground = memo(() => {
  const canvasRef = useRef(null);
  const spheresRef = useRef([]);
  const animationFrameId = useRef(null);
  const mousePosRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 }); // Start at center
  const lastTimestampRef = useRef(0);

  // Initialize spheres and canvas size
  const initializeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    spheresRef.current = sphereConfigs.map((config) => new AnimationSphere(config, rect.width, rect.height));

    const follower = spheresRef.current.find((s) => s.id === 'follower');
    if (follower) {
      const initialMouse = mousePosRef.current;
      follower.targetX = initialMouse.x;
      follower.targetY = initialMouse.y;
      follower.currentX = follower.targetX;
      follower.currentY = follower.targetY;
      follower.x = follower.currentX;
      follower.y = follower.currentY;
    }

    lastTimestampRef.current = performance.now(); // timestamp is used to ensure consistent animation speeds regardless of frame delta
  }, []);

  // Animation loop
  const animate = useCallback((timestamp) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) {
      return;
    }

    const deltaTime = timestamp - lastTimestampRef.current;
    lastTimestampRef.current = timestamp;
    const currentMousePos = mousePosRef.current;

    // Clear canvas (important: use canvas dimensions before scaling)
    ctx.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));

    // Draw each sphere
    spheresRef.current.forEach((sphere) => {
      sphere.update(deltaTime, timestamp, currentMousePos);
      sphere.draw(ctx);
    });

    animationFrameId.current = requestAnimationFrame(animate);
  }, []);

  // Effect for initialization, resize handling, and animation start/stop
  useEffect(() => {
    initializeCanvas(); // Initial setup

    // Handle mouse movement
    const handleMouseMove = (event) => {
      mousePosRef.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener('mousemove', handleMouseMove);

    // On window resize, re-initialize canvas
    const handleResize = () => {
      initializeCanvas();
    };
    window.addEventListener('resize', handleResize);

    // Start animation loop
    animationFrameId.current = requestAnimationFrame(animate);

    // Cleanup function
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
    };
  }, [initializeCanvas, animate]);

  return (
    <LoginWrapperBackgroundContainer>
      <LoginWrapperSvg />
      <StyledCanvas ref={canvasRef} />
    </LoginWrapperBackgroundContainer>
  );
});
